import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";

export default function NewLeadDialog({ users, lists, templates, onClose, onCreated }) {
  const { user: currentUser } = useAuth();
  const [fullName, setFullName] = useState("");
  const [mco, setMco] = useState((lists.mco_list || [])[0] || "");
  const [efforts, setEfforts] = useState([]);
  const [effortId, setEffortId] = useState("");
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState("");
  const [agents, setAgents] = useState([]);
  const [sourcedById, setSourcedById] = useState("");
  const [ownerId, setOwnerId] = useState((templates.find((t) => t.stage_number === 1) || {}).default_owner_id || "");
  const [fieldAgentId, setFieldAgentId] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [note, setNote] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [dup, setDup] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    const v = fullName.trim();
    const phoneDigits = phone.replace(/\D/g, "");
    const hasName = v.length >= 2;
    const hasPhone = phoneDigits.length === 10;
    const hasDob = !!dob;
    if (!hasName && !hasPhone && !hasDob) {
      setDup([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .get(`/cards/check-duplicate`, {
          params: {
            full_name: hasName ? v : undefined,
            phone: hasPhone ? phone : undefined,
            dob: hasDob ? dob : undefined,
            pipeline: "intake",
          },
        })
        .then(({ data }) => { if (!cancelled) setDup(data.matches || []); })
        .catch(() => { if (!cancelled) setDup([]); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [fullName, phone, dob]);

  useEffect(() => {
    api.get("/marketing-efforts", { params: { active: true } })
      .then(({ data }) => setEfforts(data || [])).catch(() => setEfforts([]));
    api.get("/referral-partners", { params: { active: true } })
      .then(({ data }) => setPartners(data || [])).catch(() => setPartners([]));
    api.get("/field-agents", { params: { active: true } })
      .then(({ data }) => setAgents(data || [])).catch(() => setAgents([]));
  }, []);

  const formatPhone = (v) => {
    const d = (v || "").replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.get("/lists")
      .then(({ data }) => setSourcedById((prev) => prev || data?.default_sourced_by_id || currentUser?.id || ""))
      .catch(() => setSourcedById((prev) => prev || currentUser?.id || ""));
  }, [currentUser]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = fullName.trim();
    if (v.length < 2 || v.length > 100) {
      setErr("Full name must be 2–100 characters");
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits && phoneDigits.length !== 10) {
      setErr("Phone must be a 10-digit US number (or left blank)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/cards", {
        full_name: v,
        payer_mco: mco,
        referral_partner_id: partnerId || null,
        marketing_effort_id: effortId || null,
        sourced_by_id: sourcedById || null,
        owner_id: ownerId || null,
        field_agent_id: fieldAgentId || null,
        phone: phone || "",
        dob: dob || null,
        note,
        next_action_date: nextDate || null,
      });
      toast.success(`Lead ${data.av_id} created`);
      onCreated?.(data);
      onClose();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-lead-dialog">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#15233B]/10 bg-[#F6F3EC] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#15233B]/60">New lead</div>
            <h2 className="text-lg font-extrabold tracking-tight text-[#15233B]">Add to Lead Capture</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded hover:bg-[#15233B]/5" data-testid="close-new-lead">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Full name</label>
              <input
                data-testid="new-lead-full-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="e.g., Jane Doe"
                maxLength={100}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Payer / MCO</label>
              <select
                data-testid="new-lead-mco"
                value={mco}
                onChange={(e) => setMco(e.target.value)}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                {(lists.mco_list || []).map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Referral partner (source)</label>
              <select
                data-testid="new-lead-partner"
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">— select source —</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}{p.category ? ` · ${p.category}` : ""}</option>)}
              </select>
              {partners.length === 0 && (
                <div className="mt-1 text-[10px] text-[#15233B]/55">No sources yet — add them under the Partners tab.</div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Phone</label>
              <input
                data-testid="new-lead-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder="(555) 123-4567"
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Date of birth</label>
              <input
                data-testid="new-lead-dob"
                type="date"
                value={dob}
                max={today}
                onChange={(e) => setDob(e.target.value)}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Event / Campaign (optional)</label>
              <select
                data-testid="new-lead-effort"
                value={effortId}
                onChange={(e) => setEffortId(e.target.value)}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">— none —</option>
                {efforts.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} ({o.type}){o.date ? ` · ${o.date}` : ""}
                  </option>
                ))}
              </select>
              {efforts.length === 0 && (
                <div className="mt-1 text-[10px] text-[#15233B]/55">Create events/campaigns in Settings → Events &amp; Campaigns.</div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Intake owner</label>
              <select
                data-testid="new-lead-owner"
                value={ownerId || ""}
                onChange={(e) => setOwnerId(e.target.value)}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                {users.filter((u) => u.role !== "field_agent").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Sourced by</label>
              <select
                data-testid="new-lead-sourced-by"
                value={sourcedById || ""}
                onChange={(e) => setSourcedById(e.target.value)}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">— none —</option>
                {users.filter((u) => u.role !== "field_agent").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <div className="mt-1 text-[10px] text-[#15233B]/55">Internal staff who brought this lead in (credit).</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Field agent (optional)</label>
              <select
                data-testid="new-lead-field-agent"
                value={fieldAgentId}
                onChange={(e) => setFieldAgentId(e.target.value)}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">— none —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {agents.length === 0 && (
                <div className="mt-1 text-[10px] text-[#15233B]/55">Add your agents under Partners → Field Agents.</div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Optional next-action date</label>
              <input
                data-testid="new-lead-next-date"
                type="date"
                value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Note</label>
              <textarea
                data-testid="new-lead-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
              />
            </div>
          </div>
          {dup.length > 0 && (
            <div data-testid="duplicate-warning" className="rounded-md p-3 bg-[#FBBF24]/10 border border-[#FBBF24]/40 text-[#7a5a1a] text-sm flex gap-2 items-start">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold uppercase tracking-[0.15em] text-[11px]">Possible duplicate{dup.length > 1 ? `s (${dup.length})` : ""}</div>
                {dup.slice(0, 3).map((d) => (
                  <div key={d.id} className="text-xs mt-0.5">
                    {d.full_name} · <span className="font-mono">{d.av_id}</span> · stage {d.current_stage} · {d.status === "archived" ? "archived" : "active"}
                    {Array.isArray(d.match_on) && d.match_on.length > 0 && (
                      <span className="ml-1 text-[10px] font-bold uppercase tracking-wide text-[#7a5a1a]/80">matches {d.match_on.join(" + ")}</span>
                    )}
                  </div>
                ))}
                <div className="text-[11px] italic mt-1">You can continue anyway.</div>
              </div>
            </div>
          )}
          {err && <div className="text-sm text-[#b91c1c]">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-[#15233B]/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            data-testid="new-lead-submit"
            className="px-4 py-2 rounded bg-[#FBBF24] text-white text-sm font-bold hover:bg-[#F59E0B] disabled:opacity-60"
          >
            {busy ? "Creating…" : "Create lead"}
          </button>
        </div>
      </form>
    </div>
  );
}
